// Tenant portal topbar — slim, light. Per tenant-shell-strings.md §"Topbar".
//
// Layout (left→right): hamburger (mobile) · tenant pill · lang toggle ·
// notifications bell · UserMenu. No breadcrumb, no search in V1.

import { BellIcon, LangToggle } from '@gc-coach/design-system';
import { useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import { flagFromIso } from './flag.js';
import { UserMenu } from './UserMenu.js';
import styles from './TenantTopbar.module.css';

export interface TenantTopbarProps {
  /** Unread notifications count for the bell badge. V1 placeholder = 0. */
  notificationCount?: number;
  /** Highest-priority unread item — colors the bell badge background. */
  notificationPriority?: 'info' | 'warn' | 'urgent';
  /** Hamburger click — opens the sidebar slide-over on mobile. */
  onOpenSidebar?: () => void;
}

export function TenantTopbar({
  notificationCount = 0,
  notificationPriority = 'info',
  onOpenSidebar,
}: TenantTopbarProps) {
  const { t, lang, setLang } = useI18n();
  const { session } = useSession();

  const tenantName = session?.tenant?.name ?? '';
  const jurisdictionIso = session?.tenant?.jurisdiction ?? '';
  const tenantDisplayName = tenantName.trim() || t('shell.tenantNameFallback');
  const jurisdictionFlag = jurisdictionIso ? flagFromIso(jurisdictionIso) : '';

  return (
    <header className={styles.topbar}>
      <div className={styles.left}>
        {onOpenSidebar && (
          <button
            type="button"
            className={styles.hamburger}
            onClick={onOpenSidebar}
            aria-label={t('shell.openSidebar')}
          >
            <span aria-hidden="true">☰</span>
          </button>
        )}
        <span className={styles.tenantPill}>
          {jurisdictionFlag && (
            <span className={styles.tenantFlag} aria-hidden="true">
              {jurisdictionFlag}
            </span>
          )}
          <span className={styles.tenantName}>{tenantDisplayName}</span>
        </span>
      </div>

      <div className={styles.right}>
        <LangToggle value={lang} onChange={setLang} />
        <button
          type="button"
          className={styles.bell}
          aria-label={t('shell.notificationsBellLabel')}
        >
          <BellIcon width={16} height={16} />
          {notificationCount > 0 && (
            <span
              className={[
                styles.bellBadge,
                notificationPriority === 'urgent' ? styles.bellBadgeUrgent : null,
                notificationPriority === 'warn' ? styles.bellBadgeWarn : null,
              ]
                .filter(Boolean)
                .join(' ')}
              aria-hidden="true"
            >
              {notificationCount}
            </span>
          )}
        </button>
        <UserMenu />
      </div>
    </header>
  );
}
